import { type CTNButtonProps } from '@ctn/ui/types/ctn-button';
import { type CTNOpenNotificationParams } from '@ctn/ui/types/ctn-notifications';
import { type ReactElement } from 'react';

import type { ArrayKey } from '@ctn/ui/types/generic';

type KeysNotificationParams = 'alignButtons' | 'autoHeight' | 'className' | 'closeable' | 'size' | 'reverseButtons';
type CommonNotificationParams = Pick<CTNOpenNotificationParams, KeysNotificationParams>;

const classNameComponent = 'c-pnp-notification';

/**
 * Parámetros comunes para todos los
 * tipos de notificaciones.
 */
export const commonNotificationParams: CommonNotificationParams = {
	alignButtons: 'center',
	autoHeight: true,
	className: classNameComponent,
	closeable: true,
	reverseButtons: true,
	size: 'xs',
};

/**
 * Botón de cerrado común.
 */
const closeButton = ({
	closeNotification,
	label = 'Cerrar',
	onClose,
}: {
	closeNotification: () => void;
	label?: string;
	onClose?: () => void;
}): CTNButtonProps & Pick<ArrayKey, 'key'> => ({
	key: 'cerrar',
	mode: 'tertiary',
	size: 'size-40',
	label,
	minWidth: '140',
	onClick() {
		closeNotification();
		if (onClose) {
			onClose();
		}
	},
});

export const notificationsConfig = ({
	closeNotification,
}: {
	closeNotification: () => void;
}) => ({
	errorMessage: (content: string | ReactElement, onCloseError?: () => void): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Error',
		content,
		type: 'error',
		actionButtons: [closeButton({ closeNotification, onClose: onCloseError })],
	}),
	accionIndebidaUser: (onCloseError?: () => void): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Error',
		content: (
			<div>
				<strong>Acción indebida</strong>< br/>< br/>
				<p>No se permite el guardado de esta configuración al no tener un servicio seleccionado, por favor marque el menos uno.</p>
			</div>
		),
		type: 'error',
		actionButtons: [closeButton({ closeNotification, onClose: onCloseError })],
	}),
	errorUser: (onCloseError?: () => void): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Error',
		content: (
			<div>
				<strong>No ha sido posible guardar los cambios</strong>< br/>< br/>
				<p>Por favor inténtelo de nuevo más tarde.</p>
			</div>
		),
		type: 'error',
		actionButtons: [closeButton({ closeNotification, onClose: onCloseError })],
	}),
	accionBloqueadaUser: (onCloseError?: () => void): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Error',
		content: (
			<div>
				<strong>Actualmente bloqueado</strong>< br/>< br/>
				<p>Para la activación del usuario es necesario tener un servicio activo, guarde una configuración, con al menos uno seleccionado, antes de activar.</p>
			</div>
		),
		type: 'error',
		actionButtons: [closeButton({ closeNotification, onClose: onCloseError })],
	}),
	error406: ({
		message,
		errores,
		onClickAccept,
	}: {
		message: string;
		errores: string[];
		onClickAccept?: () => void;
	}): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Error',
		content: (
			<div>
				<p><b>{message}</b></p>
				{errores.map((error: string, index: number) => (
					<p key={`${index + 1}`} className="u-mt-8">- {error}</p>
				))}
			</div>
		),
		type: 'error',
		actionButtons: [
			closeButton({ closeNotification }),
			...(onClickAccept
				? [
						{
							key: 'accept',
							label: 'Aceptar',
							mode: 'primary',
							minWidth: '140',
							size: 'size-40',
							onClick() {
								onClickAccept();
								closeNotification();
							},
						} satisfies CTNButtonProps & Pick<ArrayKey, 'key'>,
				]
				: []),
		],
	}),
	successMessage: (title: string, content: string | ReactElement): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title,
		content,
		type: 'success',
		actionButtons: [closeButton({ closeNotification })],
	}),
	adviceMessage: (content: string | ReactElement): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Aviso',
		content,
		type: 'warning',
		actionButtons: [closeButton({ closeNotification, label: 'Cerrar' })],
	}),
	erroresForm: (content: string | ReactElement, onClickClose?: () => void): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Error en el formulario',
		content,
		type: 'error',
		size: 'xs',
		actionButtons: [
			{
				key: 'cerrar',
				mode: 'tertiary',
				size: 'size-40',
				label: 'Cerrar',
				minWidth: '140',
				onClick() {
					if (onClickClose) {
						onClickClose();
					}
					closeNotification();
				},
			},
		],
	}),
	dirtyForm: (onClickAccept: () => void): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Aviso',
		size: 'xs',
		content: (
			<div>
				<p>El formulario contiene cambios sin guardar.</p>
				<p className="u-mt-8">Si pulsa el botón Continuar no se guardarán los datos introducidos.</p>
			</div>
		),
		type: 'confirm',
		actionButtons: [
			closeButton({ closeNotification }),
			{
				key: 'accept',
				label: 'Continuar',
				mode: 'danger',
				minWidth: '140',
				size: 'size-40',
				onClick() {
					onClickAccept();
					closeNotification();
				},
			},
		],
	}),
	cancelForm: (onClickAccept: () => void, onClickClose: () => void): CTNOpenNotificationParams => ({
		...commonNotificationParams,
		title: 'Cancelar',
		size: 'xs',
		content: (
			<div>
				<p>El formulario contiene cambios sin guardar.</p>
				<p className="u-mt-8">Si pulsa el botón Cerrar no se guardarán los datos introducidos.</p>
			</div>
		),
		type: 'confirm',
		actionButtons: [
			{
				key: 'accept',
				label: 'Cerrar',
				mode: 'tertiary',
				minWidth: '140',
				size: 'size-40',
				onClick() {
					onClickClose();
					closeNotification();
				},
			},
			{
				key: 'accept',
				label: 'Guardar',
				mode: 'primary',
				minWidth: '140',
				size: 'size-40',
				onClick() {
					onClickAccept();
					closeNotification();
				},
			},
		],
	}),
	removeItem({
		title,
		content,
		onAccept,
	}: {
		title: string;
		content: string;
		onAccept: () => void;
	}): CTNOpenNotificationParams {

		return {
			...commonNotificationParams,
			size: 'xs',
			title,
			content,
			type: 'confirm',
			actionButtons: [
				closeButton({ closeNotification, label: 'No' }),
				{
					key: 'accept',
					label: 'Sí',
					mode: 'danger',
					size: 'size-40',
					minWidth: '140',
					onClick() {
						onAccept();
						closeNotification();
					},
				},
			],
		};
	},
});
